import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';

export type SessionEndReason = 'revoked' | 'expired' | 'logout';
type SessionRequestScope = {
  token: string;
  controllers: Set<AbortController>;
};
type TrackedRequest = AxiosRequestConfig & {
  sessionToken?: string;
  sessionScope?: SessionRequestScope;
  sessionController?: AbortController;
  sessionDetach?: () => void;
};
const listeners = new Set<(token: string, reason: SessionEndReason) => void>();
let activeSession: SessionRequestScope | undefined;
const installed = new WeakSet<object>();
const switches = new Map<string, { reason?: SessionEndReason }>();

export function beginSessionSwitch(token: string | undefined) {
  if (!token || switches.has(token)) throw new Error('A session switch is already in progress');
  const transition: { reason?: SessionEndReason } = {};
  switches.set(token, transition);
  return (succeeded: boolean) => {
    switches.delete(token);
    if (!succeeded && transition.reason) invalidateSession(token, transition.reason);
  };
}

export function onSessionInvalidated(listener: (token: string, reason: SessionEndReason) => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function cancelSessionRequests(token: string) {
  if (activeSession?.token !== token) return;
  const obsolete = activeSession;
  activeSession = undefined;
  obsolete.controllers.forEach((controller) => controller.abort());
  obsolete.controllers.clear();
}

export function preserveSessionRequests(previousToken: string, replacementToken: string) {
  if (activeSession?.token === previousToken) activeSession.token = replacementToken;
}

export function invalidateSession(
  token: string | undefined,
  reason: SessionEndReason,
  force = false
): boolean {
  // An explicit logout still clears local state if the browser already expired its cookie.
  if (reason === 'logout' && !Cookies.get('token')) {
    if (activeSession) cancelSessionRequests(activeSession.token);
    listeners.forEach((listener) => listener('', reason));
    return true;
  }
  // The response must belong to the active credential, not to an earlier login.
  if (!token || Cookies.get('token') !== token) return false;
  const transition = switches.get(token);
  if (transition && !force && reason !== 'logout') {
    transition.reason = reason;
    return false;
  }
  cancelSessionRequests(token);
  listeners.forEach((listener) => listener(token, reason));
  return true;
}

function requestToken(config: AxiosRequestConfig): string | undefined {
  const path = config.url?.split('?')[0] || '';
  if (/\/auth\/tokenLogin\/?$/.test(path)) {
    try {
      const body = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
      return typeof body?.token === 'string' ? body.token : undefined;
    } catch {
      return undefined;
    }
  }
  // Credential failures belong to the form, even if legacy code attached a token.
  if (/\/auth\/(login|register|forget-password|change-password)\/?$/.test(path)) return undefined;
  const header = config.headers?.Authorization || config.headers?.authorization;
  const value = typeof header === 'string' ? /^Bearer\s+(.+)$/i.exec(header)?.[1] : undefined;
  return value && value !== 'undefined' && value !== 'null' ? value : undefined;
}

function finish(config?: TrackedRequest) {
  config?.sessionDetach?.();
  if (config?.sessionController) config.sessionScope?.controllers.delete(config.sessionController);
}

// SDK and legacy requests use different Axios versions; their interceptor runtime contract is shared.
export function installSessionInterceptors(client: { interceptors: unknown }) {
  if (!client?.interceptors || installed.has(client)) return;
  installed.add(client);
  const interceptors = client.interceptors as AxiosInstance['interceptors'];
  interceptors.request.use((config: TrackedRequest) => {
    const token = requestToken(config);
    if (!token) return config;
    if (Cookies.get('token') !== token) throw new axios.Cancel('Session changed before request');
    config.sessionToken = token;
    if (!activeSession || activeSession.token !== token) {
      if (activeSession) cancelSessionRequests(activeSession.token);
      activeSession = { token, controllers: new Set<AbortController>() };
    }
    config.sessionScope = activeSession;
    // A PIN transition may defer source revocation only for a bounded request.
    if (/\/auth\/pin-login\/?$/.test(config.url || ''))
      config.timeout = Math.min(config.timeout || 15_000, 15_000);
    const controller = new AbortController();
    const previousSignal = config.signal;
    const abort = () => controller.abort();
    if (previousSignal?.aborted) abort();
    previousSignal?.addEventListener?.('abort', abort);
    config.sessionDetach = () => previousSignal?.removeEventListener?.('abort', abort);
    config.sessionController = controller;
    config.signal = controller.signal;
    activeSession.controllers.add(controller);
    return config;
  });
  interceptors.response.use(
    (response) => {
      const config = response.config as TrackedRequest;
      finish(config);
      if (config.sessionScope &&
          (config.sessionScope !== activeSession || Cookies.get('token') !== config.sessionScope.token)) {
        throw new axios.Cancel('Session changed while the request was pending');
      }
      return response;
    },
    (error) => {
      const config = error?.config as TrackedRequest | undefined;
      finish(config);
      if (error?.response?.status === 401) {
        const code = error.response.data?.code || error.response.data?.error?.code;
        invalidateSession(
          config?.sessionToken || (config && requestToken(config)),
          code === 'SESSION_REVOKED' ? 'revoked' : 'expired',
          /\/auth\/pin-login\/?$/.test(config?.url || '')
        );
      }
      return Promise.reject(error);
    }
  );
}
